import { getAuthHeaders } from '../utils/authHeaders';
import { createFormPdf, yesNoColor } from '../utils/formPdf';

const API_URL = 'https://app.pvslimited.co.uk/api';

const sendPaintQualityEmail = async (formData) => {
  const calculateDFTStats = () => {
    const low = formData.dftLow ? parseFloat(formData.dftLow) : NaN;
    const high = formData.dftHigh ? parseFloat(formData.dftHigh) : NaN;
    const avg = formData.dftAverage ? parseFloat(formData.dftAverage) : NaN;
    const requiredMin = parseFloat(formData.requiredDFTMin);
    const requiredMax = parseFloat(formData.requiredDFTMax);
    let pass = 'N/A';
    if (!Number.isNaN(low) && !Number.isNaN(high) && !Number.isNaN(requiredMin) && !Number.isNaN(requiredMax)) {
      pass = low >= requiredMin && high <= requiredMax ? 'PASS' : 'FAIL';
    }
    return {
      min: !Number.isNaN(low) ? low.toFixed(1) : 'N/A',
      max: !Number.isNaN(high) ? high.toFixed(1) : 'N/A',
      avg: !Number.isNaN(avg) ? avg.toFixed(1) : 'N/A',
      pass,
    };
  };

  try {
    const dftStats = calculateDFTStats();
    const blob = await createFormPdf({
      title: 'PAINT QUALITY INSPECTION FORM',
      ref: 'Ref: PVS-PQI-001 | Version: 1.0',
      subtitle: 'LPG Vessel Paint QC Gate Documentation',
    }, (pdf) => {
      pdf.section('ASSET IDENTIFICATION');
      pdf.field('Vessel Serial', formData.vesselSerial);
      pdf.field('Vessel Water Capacity (Litres)', formData.capacityLitres);
      pdf.field('Customer Name', formData.customerName);
      pdf.field('Date Painted', formData.datePainted);
      pdf.field('Inspector Name', formData.inspectorName);
      pdf.field('Inspection Date', formData.inspectionDate);
      pdf.field('Primer Paint Used', formData.primerPaintUsed);
      pdf.field('Primer Batch Number', formData.primerBatchNumber);
      pdf.field('Topcoat Paint Used', formData.topcoatPaintUsed);
      pdf.field('Topcoat Batch Number', formData.topcoatBatchNumber);

      pdf.section('COATING APPLICATION DETAILS');
      pdf.subsection('Primer Coat');
      pdf.field('Application Method', formData.primerApplicationMethod);
      pdf.field('Primer Thinner Used', formData.primerThinnerUsed);

      pdf.subsection('Topcoat');
      pdf.field('Application Method', formData.topcoatApplicationMethod);
      pdf.field('Topcoat Thinner Used', formData.topcoatThinnerUsed);

      pdf.subsection('Oven/Bake Cycle');
      pdf.check('Primer Oven Bake Cycle Used', formData.primerOvenBakeCycleUsed);
      if (formData.primerOvenBakeCycleUsed === 'yes') {
        pdf.field('Primer Oven Temperature (C)', formData.primerOvenTemperature);
        pdf.field('Primer Bake Duration (minutes)', formData.primerOvenDuration);
      }
      pdf.check('Topcoat Oven Bake Cycle Used', formData.topcoatOvenBakeCycleUsed);
      if (formData.topcoatOvenBakeCycleUsed === 'yes') {
        pdf.field('Topcoat Oven Temperature (C)', formData.topcoatOvenTemperature);
        pdf.field('Topcoat Bake Duration (minutes)', formData.topcoatOvenDuration);
      }

      pdf.section('DRY FILM THICKNESS (DFT) READINGS');
      pdf.field('Required DFT Min (um)', formData.requiredDFTMin);
      pdf.field('Required DFT Max (um)', formData.requiredDFTMax);
      pdf.subsection('Actual DFT Readings (um)');
      pdf.field('Low', formData.dftLow);
      pdf.field('High', formData.dftHigh);
      pdf.field('Average', formData.dftAverage);
      pdf.subsection('DFT Statistics (Auto-calculated)');
      pdf.field('Minimum DFT', `${dftStats.min} um`);
      pdf.field('Maximum DFT', `${dftStats.max} um`);
      pdf.field('Average DFT', `${dftStats.avg} um`);
      pdf.field('Result', dftStats.pass, yesNoColor(dftStats.pass));

      pdf.section('VISUAL INSPECTION CHECKLIST');
      pdf.check('Coverage Uniform', formData.coverageUniform);
      pdf.check('Runs or Sags', formData.runsOrSags);
      pdf.check('Pinholes', formData.pinholes);
      pdf.check('Orange Peel', formData.orangePeel);
      pdf.check('Dry Spray', formData.drySpray);
      pdf.check('Underside Fully Covered', formData.undersideFullyCovered);
      pdf.check('Data Plates and Valve Threads Free of Overspray', formData.dataPlatesFreeOfOverspray);

      pdf.section('RELEASE DECISION');
      pdf.field('Final Status', formData.finalStatus, yesNoColor(formData.finalStatus));
      pdf.field('Release Notes', formData.releaseNotes);

      if (formData.additionalComments) {
        pdf.section('ADDITIONAL COMMENTS');
        pdf.longText('', formData.additionalComments);
      }
    });

    const formDataToSend = new FormData();
    formDataToSend.append('file', blob, `Paint_Quality_Inspection_${formData.vesselSerial}_${Date.now()}.pdf`);
    formDataToSend.append('vesselSerial', formData.vesselSerial);
    formDataToSend.append('customerName', formData.customerName);
    formDataToSend.append('inspectorName', formData.inspectorName);
    formDataToSend.append('inspectionDate', formData.inspectionDate);
    formDataToSend.append('finalStatus', formData.finalStatus);
    formDataToSend.append('formType', 'paint-quality-inspection');

    const response = await fetch(`${API_URL}/send-paint-quality-email`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formDataToSend,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error in sendPaintQualityEmail:', error);
    throw error;
  }
};

export default sendPaintQualityEmail;
